import { useState } from 'react';
import { localeUI } from '../lib/format';
import { CalendarDays, List, X } from 'lucide-react';
import { useI18n } from '../lib/i18n';

type CalMode = 'all' | 'day' | 'week' | 'month';

const money = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Módulo "vista-calendario" REUSABLE (genérico). Total + buscador + flechas para
 * deslizar por periodo (day/week/month) o rango de fechas, sobre cualquier lista.
 * Filtra por la fecha que devuelva `dateOf`. Default = "all". En modo month el mes
 * sale grande. Se le pasa cómo sacar la fecha/monto/texto de búsqueda y cómo
 * renderizar cada item. Si no se pasa `amountOf`, no muestra total en dólares.
 */
export function CalendarView<T>({ items, dateOf, amountOf, searchText, renderItem, totalLabel, countLabel, view = 'cards', initialMode = 'all', initialOffset = 0, fullWidth = false, grid = false, hideSearch = false }: {
  items: T[];
  dateOf: (x: T) => string | null;
  amountOf?: (x: T) => number;
  searchText: (x: T) => string;
  /** La página ya tiene su buscador arriba (junto a los demás filtros): no dibujar otro. */
  hideSearch?: boolean;
  renderItem: (x: T) => React.ReactNode;
  totalLabel?: string;
  countLabel?: string;
  view?: 'lines' | 'cards';
  initialMode?: CalMode;
  initialOffset?: number;
  fullWidth?: boolean;
  /**
   * Dibuja una GRILLA de días (semana/mes) en vez de una lista filtrada por fecha. Se pidió para
   * Insurance inspection: con 50 inspecciones, lo que hace falta es ver cómo caen en la semana,
   * no leerlas en fila. El toggle 🗓/☰ deja elegir; sin esta prop, todo sigue como estaba.
   */
  grid?: boolean;
}) {
  const { t } = useI18n();
  const [mode, setMode] = useState<CalMode>(initialMode);
  // La grilla solo tiene sentido en semana/mes: en 'day' y 'all' no hay nada que cuadricular.
  const [asGrid, setAsGrid] = useState(grid);
  const [offset, setOffset] = useState(initialOffset);
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const useRange = Boolean(from && to);
  const gridOn = asGrid && (mode === 'week' || mode === 'month') && !useRange;

  const now = new Date();
  const sod = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  let start: Date | null = null, end: Date | null = null;
  if (useRange) {
    start = new Date(`${from}T00:00:00`);
    const e = new Date(`${to}T00:00:00`); end = new Date(e.getFullYear(), e.getMonth(), e.getDate() + 1);
  } else if (mode === 'day') {
    const b = sod(now); b.setDate(b.getDate() + offset); start = b; end = new Date(b.getFullYear(), b.getMonth(), b.getDate() + 1);
  } else if (mode === 'week') {
    const b = sod(now); b.setDate(b.getDate() - b.getDay() + offset * 7); start = b; end = new Date(b.getFullYear(), b.getMonth(), b.getDate() + 7);
  } else if (mode === 'month') {
    start = new Date(now.getFullYear(), now.getMonth() + offset, 1); end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 1);
  }

  const sq = q.trim().toLowerCase().replace(/[$,]/g, '');
  const inWindow = (x: T) => {
    if (!start || !end) return true;
    const ds = dateOf(x); if (!ds) return false;
    const d = new Date(ds); return d >= start && d < end;
  };
  const matchText = (x: T) => !sq || searchText(x).toLowerCase().includes(sq);
  const filtered = items.filter((x) => inWindow(x) && matchText(x));
  const total = amountOf ? filtered.reduce((s, x) => s + amountOf(x), 0) : null;
  const canNav = mode !== 'all' && !useRange;

  const periodLabel = () => {
    if (useRange) return `${from} → ${to}`;
    if (mode === 'all') return t('cal.all');
    if (mode === 'month') return start!.toLocaleDateString(localeUI(), { month: 'long', year: 'numeric' });
    if (mode === 'week') return `${start!.toLocaleDateString(localeUI(), { month: 'short', day: 'numeric' })} – ${new Date(end!.getTime() - 86400000).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric' })}`;
    return start!.toLocaleDateString(localeUI(), { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };
  const modes: CalMode[] = ['all', 'day', 'week', 'month'];

  return (
    <div style={{ display: 'grid', gap: 12, maxWidth: fullWidth ? undefined : (view === 'lines' ? 1040 : undefined) }}>
      <div className="card" style={{ display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {total != null ? (
            <div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{totalLabel}</div>
              <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-bold)', color: 'var(--green)', fontFamily: 'var(--font-mono)' }}>{money(total)}</div>
            </div>
          ) : (
            <div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{countLabel ?? totalLabel}</div>
              <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-bold)' }}>{filtered.length}</div>
            </div>
          )}
          {total != null && <span className="badge gray">{filtered.length}</span>}
          <span style={{ flex: 1 }} />
          {!hideSearch && <input className="input sm" style={{ maxWidth: 240 }} placeholder={t('claims.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {canNav && <button className="btn ghost sm" onClick={() => setOffset((o) => o - 1)} title={t('cal.prev')}>←</button>}
          <div style={{ fontSize: mode === 'month' && !useRange ? 'var(--fs-lg)' : 'var(--fs-base)', fontWeight: mode === 'month' && !useRange ? 'var(--fw-bold)' : 'var(--fw-semibold)', textTransform: mode === 'month' ? 'capitalize' : 'none', minWidth: 150 }}>{periodLabel()}</div>
          {canNav && <button className="btn ghost sm" onClick={() => setOffset((o) => o + 1)} title={t('cal.next')}>→</button>}
          {canNav && offset !== 0 && <button className="btn ghost sm" onClick={() => setOffset(0)}>{t('cal.today')}</button>}
          <span style={{ flex: 1 }} />
          <div style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
            {modes.map((m) => (
              <button key={m} className={`btn ghost sm${mode === m && !useRange ? ' active' : ''}`} style={{ borderRadius: 0 }}
                onClick={() => { setMode(m); setOffset(0); setFrom(''); setTo(''); }}>{t(`cal.${m}`)}</button>
            ))}
          </div>
          {(mode === 'week' || mode === 'month') && !useRange && (
            <button
              className={`btn ghost sm${asGrid ? ' active' : ''}`}
              title={asGrid ? t('cal.asList') : t('cal.asGrid')}
              onClick={() => setAsGrid((v) => !v)}
            >{asGrid ? <List size={14} strokeWidth={1.75} /> : <CalendarDays size={14} strokeWidth={1.75} />}</button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input className="input sm" type="date" style={{ width: 138 }} value={from} onChange={(e) => setFrom(e.target.value)} />
            <span className="muted">→</span>
            <input className="input sm" type="date" style={{ width: 138 }} value={to} onChange={(e) => setTo(e.target.value)} />
            {useRange && <button className="btn ghost sm" onClick={() => { setFrom(''); setTo(''); }}><X size={12} strokeWidth={1.75} /></button>}
          </div>
        </div>
      </div>
      {/* LO QUE EL PERÍODO ESCONDE, dicho en voz alta (reporte de Manuel 2026-08-19: "dice que
          tiene 52 y me muestra solo 3", y Sayna veía un cliente que él no — estaba en otro mes).
          Sin este renglón, un filtro de fecha que arranca solo se lee como una lista incompleta. */}
      {(() => {
        const fuera = items.filter(matchText).length - filtered.length;
        if (fuera <= 0 || mode === 'all') return null;
        return (
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: -4 }}>
            <span>{t('cal.hiddenByPeriod', { n: String(fuera), period: periodLabel() })}</span>
            <button className="btn ghost sm" onClick={() => { setMode('all'); setOffset(0); setFrom(''); setTo(''); }}>{t('cal.seeAll')}</button>
          </div>
        );
      })()}
      {gridOn ? (
        <DayGrid start={start!} end={end!} items={filtered} dateOf={dateOf} renderItem={renderItem} />
      ) : filtered.length === 0 ? <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('cal.empty')}</div> : (
        <div style={view === 'lines'
          ? { display: 'grid', gap: 10 }
          : { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12, alignItems: 'start' }}>
          {filtered.map((x) => renderItem(x))}
        </div>
      )}
    </div>
  );
}


/**
 * La grilla de días: una celda por día del rango, con lo que cae en cada una.
 *
 * Arranca en domingo y completa la semana hacia atrás y hacia adelante, así las columnas quedan
 * alineadas por día de semana (que es para lo que sirve mirar una grilla: ver que el martes está
 * cargado y el jueves vacío). Los días fuera del rango pedido se pintan apagados pero se muestran:
 * una grilla con huecos se lee peor que una con días en gris.
 */
function DayGrid<T>({ start, end, items, dateOf, renderItem }: {
  start: Date; end: Date; items: T[];
  dateOf: (x: T) => string | null;
  renderItem: (x: T) => React.ReactNode;
}) {
  const { t } = useI18n();
  const key = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const porDia = new Map<string, T[]>();
  for (const x of items) {
    const raw = dateOf(x);
    if (!raw) continue;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) continue;
    const k = key(d);
    const l = porDia.get(k) ?? [];
    l.push(x);
    porDia.set(k, l);
  }

  // Se completa hasta domingo/sábado para que las columnas sean días de semana de verdad.
  const desde = new Date(start); desde.setDate(desde.getDate() - desde.getDay());
  const hasta = new Date(end); hasta.setDate(hasta.getDate() + (6 - ((hasta.getDate() === end.getDate() ? end.getDay() : hasta.getDay()) || 0)));
  const dias: Date[] = [];
  for (let d = new Date(desde); d < hasta && dias.length < 42; d.setDate(d.getDate() + 1)) dias.push(new Date(d));

  const hoy = key(new Date());
  const dow = [0, 1, 2, 3, 4, 5, 6].map((i) => {
    const d = new Date(2026, 1, 1 + i); // 2026-02-01 fue domingo
    return d.toLocaleDateString(localeUI(), { weekday: 'short' });
  });

  return (
    <div className="card" style={{ padding: 8, overflowX: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(120px, 1fr))', gap: 6, minWidth: 860 }}>
        {dow.map((n) => (
          <div key={n} className="muted" style={{ fontSize: 'var(--fs-2xs)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 'var(--fw-bold)', padding: '2px 4px' }}>{n}</div>
        ))}
        {dias.map((d) => {
          const k = key(d);
          const lista = porDia.get(k) ?? [];
          const fuera = d < start || d >= end;
          return (
            <div
              key={k}
              style={{
                minHeight: 96, borderRadius: 8, padding: 6,
                border: `1px solid ${k === hoy ? 'var(--accent)' : 'var(--line)'}`,
                background: fuera ? 'var(--surface-2)' : 'var(--card)',
                opacity: fuera ? 0.55 : 1,
                display: 'grid', gap: 4, alignContent: 'start',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: k === hoy ? 'var(--fw-bold)' : 'var(--fw-semibold)', color: k === hoy ? 'var(--accent)' : undefined }}>{d.getDate()}</span>
                {lista.length > 0 && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{lista.length}</span>}
              </div>
              {lista.map((x) => renderItem(x))}
            </div>
          );
        })}
      </div>
      {items.length === 0 && <div className="muted" style={{ fontSize: 'var(--fs-sm)', padding: 10 }}>{t('cal.empty')}</div>}
    </div>
  );
}
